import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Package, DollarSign, User, LogOut, CreditCard, Building2, AlertTriangle, CheckCircle, XCircle, Menu, X, ShoppingBag } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState('drops');
  const [myDrops, setMyDrops] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [notificationsOptIn, setNotificationsOptIn] = useState(true);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [selectedDropForWithdraw, setSelectedDropForWithdraw] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPaymentInfo();
    fetchMyOrders();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/my-drops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyDrops(response.data);
      
      // Get user notification preferences
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationsOptIn(userRes.data.notifications_opt_in ?? true);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payments/info`);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch payment info', error);
    }
  };

  const fetchMyOrders = async () => {
    try {
      const response = await axios.get(`${API}/my-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders', error);
    }
  };

  const handleToggleNotifications = async (checked) => {
    try {
      await axios.patch(`${API}/users/me/notifications?opt_in=${checked}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationsOptIn(checked);
      toast.success(checked ? 'Notifications enabled' : 'Notifications disabled');
    } catch (error) {
      toast.error('Failed to update notification preferences');
    }
  };

  const handleWithdraw = async () => {
    if (!selectedDropForWithdraw) return;
    
    setWithdrawing(true);
    try {
      await axios.post(`${API}/drops/${selectedDropForWithdraw.id}/withdraw`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Successfully withdrawn from drop');
      setShowWithdrawDialog(false);
      setSelectedDropForWithdraw(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Open', variant: 'default', className: 'bg-green-100 text-green-800' },
      confirming: { label: 'Confirming', variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
      ordered: { label: 'Ordered', variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
      in_transit: { label: 'In Transit', variant: 'secondary', className: 'bg-purple-100 text-purple-800' },
      ready_for_collection: { label: 'Ready for Collection', variant: 'secondary', className: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Completed', variant: 'secondary', className: 'bg-gray-100 text-gray-800' },
      not_proceeding: { label: 'Not Proceeding', variant: 'destructive', className: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary', className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const canWithdraw = (status) => {
    return ['open', 'confirming'].includes(status);
  };

  const isDropClosed = (status) => {
    return ['ordered', 'in_transit', 'ready_for_collection', 'completed'].includes(status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="dashboard-page">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-primary text-white">
        <Link to="/" className="text-xl font-playfair font-bold">sharedequal</Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar - Hidden on mobile unless toggled */}
        <aside className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50
          w-56 min-h-screen bg-primary text-primary-foreground p-6 flex flex-col
          transition-transform duration-300 ease-in-out
        `}>
          <div className="mb-8 hidden lg:block">
            <Link to="/" className="text-2xl font-playfair font-bold tracking-tight">
              sharedequal
            </Link>
          </div>
          
          <nav className="space-y-2 flex-1 mt-4 lg:mt-0">
            <button
              onClick={() => { setActiveTab('drops'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === 'drops' ? 'bg-white/20' : 'hover:bg-white/10'
              }`}
              data-testid="nav-drops"
            >
              <Package className="w-5 h-5" />
              My Drops
            </button>
            <button
              onClick={() => { setActiveTab('orders'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === 'orders' ? 'bg-white/20' : 'hover:bg-white/10'
              }`}
              data-testid="nav-orders"
            >
              <ShoppingBag className="w-5 h-5" />
              My Orders
            </button>
            <button
              onClick={() => { setActiveTab('payments'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === 'payments' ? 'bg-white/20' : 'hover:bg-white/10'
              }`}
              data-testid="nav-payments"
            >
              <DollarSign className="w-5 h-5" />
              Payment Info
            </button>
            <button
              onClick={() => { setActiveTab('profile'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === 'profile' ? 'bg-white/20' : 'hover:bg-white/10'
              }`}
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" />
              Profile
            </button>
          </nav>
          
          {isAdmin && (
            <Link to="/admin" className="block mb-4">
              <Button variant="secondary" className="w-full">
                Admin Panel
              </Button>
            </Link>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-white/10 transition-colors text-primary-foreground/80"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 lg:ml-0">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6 lg:mb-8">
              <h1 className="text-2xl lg:text-4xl font-playfair font-bold text-gray-900">
                Welcome, {user?.full_name || 'User'}
              </h1>
              <p className="text-gray-600 mt-1 text-sm lg:text-base">Manage your drops and payments</p>
            </div>

            {/* My Drops Tab */}
            {activeTab === 'drops' && (
              <div className="space-y-4 lg:space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-xl lg:text-2xl font-semibold">My Drops</h2>
                  <Link to="/calendar">
                    <Button>Join a Drop</Button>
                  </Link>
                </div>

                {myDrops.length === 0 ? (
                  <Card className="border-stone-200">
                    <CardContent className="p-8 lg:p-12 text-center">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No drops yet</p>
                      <p className="text-gray-400 mt-2">Join a drop to get started</p>
                      <Link to="/calendar" className="mt-4 inline-block">
                        <Button>Browse Drops</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {myDrops.map((drop) => (
                      <Card key={drop.id} className="border-stone-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl">{drop.brand}</CardTitle>
                              <CardDescription>{drop.name}</CardDescription>
                            </div>
                            {getStatusBadge(drop.status)}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {/* Submission Details */}
                            {drop.submission && (
                              <div className="bg-stone-50 p-4 rounded-lg">
                                <h4 className="font-medium mb-2">Your Items</h4>
                                <ul className="text-sm space-y-1">
                                  {drop.submission.items?.map((item, i) => (
                                    <li key={i} className="flex justify-between">
                                      <span>{item.category} - {item.name}</span>
                                      <span>Qty: {item.quantity}</span>
                                    </li>
                                  ))}
                                </ul>
                                {drop.submission.solo_estimate > 0 && (
                                  <p className="text-sm mt-2 text-gray-600">
                                    Solo Estimate: SGD {drop.submission.solo_estimate}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Status-specific messages */}
                            {isDropClosed(drop.status) && (
                              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                                <p className="text-blue-800 text-sm flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4" />
                                  Drop closed. Order has been placed.
                                </p>
                              </div>
                            )}

                            {drop.status === 'not_proceeding' && (
                              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                <p className="text-red-800 text-sm flex items-center gap-2">
                                  <XCircle className="w-4 h-4" />
                                  This drop did not reach minimum threshold and will not proceed.
                                </p>
                              </div>
                            )}

                            {/* Withdrawal Button */}
                            {canWithdraw(drop.status) && (
                              <div className="pt-4 border-t border-stone-200">
                                <Button
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => {
                                    setSelectedDropForWithdraw(drop);
                                    setShowWithdrawDialog(true);
                                  }}
                                >
                                  Withdraw from Drop
                                </Button>
                              </div>
                            )}

                            {/* Payment info for ordered drops */}
                            {drop.status === 'ordered' && (
                              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                                <h4 className="font-medium text-amber-800 mb-2">Deposit Required</h4>
                                <p className="text-sm text-amber-700">
                                  50% deposit is now due. Payment confirms your participation. 
                                  Shipping, service fees and GST will be collected upon collection.
                                </p>
                                <Button 
                                  className="mt-3"
                                  onClick={() => setActiveTab('payments')}
                                >
                                  View Payment Info
                                </Button>
                              </div>
                            )}

                            {drop.status === 'ready_for_collection' && (
                              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                                <h4 className="font-medium text-orange-800 mb-2">Balance Due</h4>
                                <p className="text-sm text-orange-700">
                                  Your order is ready for collection. Please pay the remaining balance 
                                  including shipping, service fees and GST.
                                </p>
                                <Button 
                                  className="mt-3"
                                  onClick={() => setActiveTab('payments')}
                                >
                                  View Payment Info
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Withdrawal Disclaimer */}
                <div className="bg-stone-100 p-4 rounded-lg text-sm text-gray-600">
                  <p className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      You may withdraw before the drop is ordered. Once the drop status changes to Ordered, 
                      participation becomes binding and withdrawal is no longer permitted.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Payment Info Tab */}
            {activeTab === 'payments' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold">Payment Methods</h2>
                
                {/* Payment Structure Info */}
                <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
                  <h3 className="font-semibold text-amber-800 mb-3">Payment Structure</h3>
                  <ul className="text-sm text-amber-700 space-y-2">
                    <li>• <strong>50% deposit</strong> (item value only) - collected when drop is Ordered</li>
                    <li>• <strong>Remaining 50% + Shipping + Service Fees + GST</strong> - collected when Ready for Collection</li>
                  </ul>
                  <p className="text-sm text-amber-600 mt-4 italic">
                    GST applicable to shipping services will be included in the remaining balance payable upon collection.
                  </p>
                </div>

                {paymentInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {paymentInfo.payment_methods.map((method) => (
                      <Card key={method.type} className="border-stone-200">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            {method.type === 'paynow' ? (
                              <CreditCard className="w-5 h-5 text-primary" />
                            ) : (
                              <Building2 className="w-5 h-5 text-primary" />
                            )}
                            {method.name}
                          </CardTitle>
                          <CardDescription>{method.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {method.type === 'paynow' && (
                            <div className="space-y-3">
                              {method.details.pending_message ? (
                                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                                  <p className="text-blue-800 text-sm font-medium">
                                    {method.details.pending_message}
                                  </p>
                                </div>
                              ) : (
                                <>
                                  <div className="bg-stone-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500">UEN</p>
                                    <p className="font-mono text-lg font-semibold">{method.details.uen}</p>
                                  </div>
                                  <div className="bg-stone-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-500">Recipient</p>
                                    <p className="font-semibold">{method.details.recipient_name}</p>
                                  </div>
                                </>
                              )}
                              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside mt-4">
                                {method.details.instructions.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                          {method.type === 'cardup' && (
                            <div className="space-y-3">
                              {method.details.pending_message && (
                                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                                  <p className="text-blue-800 text-sm font-medium">
                                    {method.details.pending_message}
                                  </p>
                                </div>
                              )}
                              <p className="text-sm text-gray-600">
                                Use CardUp to pay with your credit card. Processing fees apply.
                              </p>
                              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside mt-2">
                                {method.details.instructions.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                {paymentInfo?.notes && (
                  <div className="bg-stone-100 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">Important Notes</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {paymentInfo.notes.map((note, i) => (
                        <li key={i}>• {note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab - Purchase History */}
            {activeTab === 'orders' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold">My Orders</h2>
                <p className="text-muted-foreground">View your purchase history across all drops</p>

                {myOrders.length === 0 ? (
                  <Card className="border-stone-200">
                    <CardContent className="p-8 lg:p-12 text-center">
                      <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No orders yet</p>
                      <p className="text-gray-400 mt-2">Your purchase history will appear here once you participate in a drop</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {myOrders.map((order) => (
                      <Card key={order.id} className="border-stone-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-xl">{order.drop_brand}</CardTitle>
                              <CardDescription>{order.drop_name}</CardDescription>
                            </div>
                            {order.drop_status && getStatusBadge(order.drop_status)}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Items List */}
                            <div>
                              <h4 className="font-medium text-sm text-gray-500 mb-3">Items Purchased</h4>
                              {order.items && order.items.length > 0 ? (
                                <ul className="space-y-2">
                                  {order.items.map((item, i) => (
                                    <li key={i} className="flex justify-between text-sm bg-stone-50 p-2 rounded">
                                      <span>{item.name} ({item.category}) x{item.quantity}</span>
                                      <span className="font-medium">SGD {(item.unit_price * item.quantity).toFixed(2)}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-gray-400">No items listed</p>
                              )}
                            </div>

                            {/* Cost Breakdown */}
                            <div className="bg-stone-50 p-4 rounded-lg">
                              <h4 className="font-medium text-sm text-gray-500 mb-3">Cost Breakdown</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span>Item Total:</span>
                                  <span>SGD {order.item_total?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Shipping Fee:</span>
                                  <span>SGD {order.shipping_fee?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Service Fee:</span>
                                  <span>SGD {order.service_fee?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="flex justify-between font-bold pt-2 border-t border-stone-200">
                                  <span>Grand Total:</span>
                                  <span>SGD {order.grand_total?.toFixed(2) || '0.00'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {order.notes && (
                            <p className="text-sm text-gray-500 mt-4">Notes: {order.notes}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold">Profile</h2>
                
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle>Account Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-gray-500">Full Name</Label>
                      <p className="font-medium">{user?.full_name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Email</Label>
                      <p className="font-medium">{user?.email}</p>
                    </div>
                    {user?.phone && (
                      <div>
                        <Label className="text-gray-500">Phone</Label>
                        <p className="font-medium">{user.phone}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle>Notification Preferences</CardTitle>
                    <CardDescription>
                      Control how you receive updates about new drops
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="notifications" className="font-medium">
                          New Drop Notifications
                        </Label>
                        <p className="text-sm text-gray-500">
                          Receive email when new drops are launched
                        </p>
                      </div>
                      <Switch
                        id="notifications"
                        checked={notificationsOptIn}
                        onCheckedChange={handleToggleNotifications}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Withdrawal Confirmation Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Withdrawal</DialogTitle>
            <DialogDescription>
              Are you sure you want to withdraw from {selectedDropForWithdraw?.brand}?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg my-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> You may withdraw before the drop is ordered. 
              Once the drop status changes to Ordered, participation becomes binding.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleWithdraw}
              disabled={withdrawing}
            >
              {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
